import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Clock, AlertTriangle } from "lucide-react";

interface IdleTimeoutWarningProps {
  open: boolean;
  secondsRemaining: number;
  onStayLoggedIn: () => void;
}

export function IdleTimeoutWarning({ 
  open, 
  secondsRemaining, 
  onStayLoggedIn 
}: IdleTimeoutWarningProps) {
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    return `${secs} seconds`;
  };

  return (
    <AlertDialog open={open}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-full">
              <Clock className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            </div>
            <AlertDialogTitle className="text-xl">Session Timeout Warning</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="space-y-4">
            <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800 dark:text-amber-200">
                <p className="font-medium">21 CFR Part 11 Compliance</p>
                <p className="mt-1 text-amber-700 dark:text-amber-300">
                  For security purposes, your session will expire due to inactivity.
                </p>
              </div>
            </div>
            
            <div className="text-center py-4">
              <p className="text-3xl font-bold text-destructive">
                {formatTime(secondsRemaining)}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                until automatic logout
              </p>
            </div>
            
            <p className="text-sm text-muted-foreground">
              Click "Stay Logged In" to continue your session, or you will be 
              automatically logged out when the timer expires.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction 
            onClick={onStayLoggedIn}
            className="w-full"
          >
            Stay Logged In
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
